import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * Curseur d'intervalle de prix pour le filtre de collection.
 *
 * Deux <input type="range"> superposés pilotent les deux champs texte du
 * filtre Shopify, qui restent la source de vérité du formulaire. On écrit dans
 * ces champs pendant le glissement, et on laisse l'événement `change` du
 * curseur remonter jusqu'à <price-facet-component>, qui relance la requête.
 *
 * Les champs affichent toujours un montant : ce sont leurs attributs `name`
 * qu'on pose ou qu'on retire selon qu'une borne filtre vraiment, sinon chaque
 * requête traînerait un intervalle 0 → maximum qui ne filtre rien.
 */
class CpPriceSlider extends HTMLElement {
  connectedCallback() {
    this.curseurMin = this.querySelector('[data-borne="min"]');
    this.curseurMax = this.querySelector('[data-borne="max"]');
    this.etiquetteMin = this.querySelector('[data-etiquette="min"]');
    this.etiquetteMax = this.querySelector('[data-etiquette="max"]');
    this.piste = this.querySelector('.price-slider__piste');
    this.poigneeActive = null;

    // Hors collection (filtre de la recherche), un simple conteneur [data-price-slider-host].
    const facette = this.closest('price-facet-component, [data-price-slider-host]');
    this.facette = facette;
    this.champMin = facette?.querySelector('input[ref="minInput"]');
    this.champMax = facette?.querySelector('input[ref="maxInput"]');

    if (!this.curseurMin || !this.curseurMax || !this.champMin || !this.champMax) return;

    this.devise = facette?.dataset.currency || 'EUR';
    this.langue = document.documentElement.lang || 'fr';
    this.formatArgent = this.#extraireFormat(facette?.dataset.moneyFormat);
    // Combien d'unités mineures valent une unité (100 centimes pour l'euro,
    // 1 pour le yen) : c'est ce qui traduit les valeurs du curseur, exprimées
    // en unités entières, vers les montants attendus par le thème.
    this.diviseur = convertMoneyToMinorUnits('1', this.devise) || 1;

    this.addEventListener('input', this.#surGlissement);
    this.addEventListener('pointerdown', this.#surAppui);
    // En capture : le gestionnaire du thème écoute `change` sur la facette et
    // construit son FormData aussitôt. On doit avoir posé les `name` avant lui.
    facette?.addEventListener('input', this.#surSaisie, true);
    facette?.addEventListener('change', this.#surSaisie, true);

    // Après un filtrage, la section est re-rendue : les attributs reprennent
    // les valeurs du serveur, mais les propriétés des curseurs ne suivent pas.
    // On n'observe QUE ces attributs : surveiller le sous-arbre ferait réagir
    // l'observateur à nos propres écritures de montants, et chaque glissement
    // serait aussitôt ramené à la valeur du serveur.
    this.observateur = new MutationObserver(() => this.#resynchroniser());
    this.observateur.observe(facette ?? this, {
      subtree: true,
      attributes: true,
      attributeFilter: ['value'],
    });

    this.#rafraichir();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.#surGlissement);
    this.removeEventListener('pointerdown', this.#surAppui);
    this.#lacherPointeur();
    this.facette?.removeEventListener('input', this.#surSaisie, true);
    this.facette?.removeEventListener('change', this.#surSaisie, true);
    this.observateur?.disconnect();
  }

  /**
   * Isole le gabarit de montant du format de la boutique (« {{amount}} »).
   * @param {string | undefined} format
   * @returns {string}
   */
  #extraireFormat(format) {
    const trouve = (format ?? '').match(/{{\s*\w+\s*}}/);
    return trouve ? trouve[0] : '{{amount}}';
  }

  /**
   * Appui sur le rail : la poignée la plus proche du point vient s'y poser, et
   * elle suit ensuite le doigt tant qu'il ne se lève pas.
   *
   * On n'utilise pas le glissement natif des <input type="range"> : il oblige à
   * viser la poignée, et sur mobile un glissement de droite à gauche parti près
   * du bord se fait avaler par le geste de retour arrière du navigateur. En
   * capturant le pointeur dès l'appui, le geste nous appartient jusqu'au bout.
   */
  #surAppui = (evenement) => {
    if (evenement.button > 0 || !this.#dansLaZone(evenement.clientY)) return;

    const valeur = this.#valeurAuPoint(evenement.clientX);
    if (valeur == null) return;

    this.poigneeActive = this.#poigneeLaPlusProche(valeur);
    this.valeurAvantGeste = this.poigneeActive.value;

    // Empêche la sélection de texte et le défilement pendant le glissement.
    evenement.preventDefault();
    try {
      this.setPointerCapture(evenement.pointerId);
      this.pointeurCapture = evenement.pointerId;
    } catch {
      // Pointeur déjà relâché : on suit le geste sans capture plutôt que
      // d'abandonner l'appui.
      this.pointeurCapture = null;
    }
    this.classList.add('price-slider--glissement');
    this.addEventListener('pointermove', this.#surDeplacement);
    this.addEventListener('pointerup', this.#surRelachement);
    this.addEventListener('pointercancel', this.#surRelachement);

    this.#deplacer(valeur);
  };

  #surDeplacement = (evenement) => {
    const valeur = this.#valeurAuPoint(evenement.clientX);
    if (valeur == null) return;
    evenement.preventDefault();
    this.#deplacer(valeur);
  };

  #surRelachement = () => {
    const poignee = this.poigneeActive;
    const valeurAvant = this.valeurAvantGeste;
    this.#lacherPointeur();

    // Un appui qui n'a rien bougé ne doit pas relancer la requête de filtrage.
    if (poignee && poignee.value !== valeurAvant) {
      poignee.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  #lacherPointeur() {
    this.removeEventListener('pointermove', this.#surDeplacement);
    this.removeEventListener('pointerup', this.#surRelachement);
    this.removeEventListener('pointercancel', this.#surRelachement);
    if (this.pointeurCapture != null && this.hasPointerCapture(this.pointeurCapture)) {
      this.releasePointerCapture(this.pointeurCapture);
    }
    this.pointeurCapture = null;
    this.poigneeActive = null;
    this.valeurAvantGeste = null;
    this.classList.remove('price-slider--glissement');
  }

  /**
   * Bande sensible : les 24 px des poignées plus 10 px de tolérance, pour viser
   * large au doigt sans que le tapotement des deux montants déplace une borne.
   * @param {number} ordonnee
   * @returns {boolean}
   */
  #dansLaZone(ordonnee) {
    const bande = this.curseurMin.getBoundingClientRect();
    return ordonnee >= bande.top && ordonnee <= bande.bottom + 10;
  }

  /**
   * Convertit une abscisse écran en valeur du curseur, en tenant compte du sens
   * d'écriture (le rail est inversé en arabe).
   * @param {number} abscisse
   * @returns {number | null}
   */
  #valeurAuPoint(abscisse) {
    const rail = this.piste ?? this;
    const cadre = rail.getBoundingClientRect();
    if (!cadre.width) return null;

    let position = (abscisse - cadre.left) / cadre.width;
    if (getComputedStyle(this).direction === 'rtl') position = 1 - position;
    position = Math.min(Math.max(position, 0), 1);

    const minimum = Number(this.curseurMin.min);
    const maximum = Number(this.curseurMax.max);
    return Math.round(minimum + position * (maximum - minimum));
  }

  /**
   * Poignée à déplacer pour une valeur visée : la plus proche, et celle du bon
   * côté quand le point tombe hors de l'intervalle sélectionné.
   * @param {number} valeur
   * @returns {HTMLInputElement}
   */
  #poigneeLaPlusProche(valeur) {
    const bas = Number(this.curseurMin.value);
    const haut = Number(this.curseurMax.value);

    if (valeur <= bas) return this.curseurMin;
    if (valeur >= haut) return this.curseurMax;
    return valeur - bas <= haut - valeur ? this.curseurMin : this.curseurMax;
  }

  /**
   * Pose la poignée active sur une valeur, sans franchir l'autre poignée.
   * @param {number} valeur
   */
  #deplacer(valeur) {
    const poignee = this.poigneeActive;
    if (!poignee) return;

    const borne =
      poignee === this.curseurMin
        ? Math.min(valeur, Number(this.curseurMax.value))
        : Math.max(valeur, Number(this.curseurMin.value));

    if (poignee.value === String(borne)) return;
    poignee.value = String(borne);
    // Même chemin que le glissement natif : #surGlissement recopie l'intervalle
    // dans les champs du formulaire et rafraîchit le remplissage.
    poignee.dispatchEvent(new Event('input', { bubbles: true }));
  }

  #surGlissement = (evenement) => {
    if (evenement.target !== this.curseurMin && evenement.target !== this.curseurMax) return;

    let bas = Number(this.curseurMin.value);
    let haut = Number(this.curseurMax.value);

    // Les deux poignées ne se croisent pas : celle qu'on pousse repousse l'autre.
    if (bas > haut) {
      if (evenement.target === this.curseurMin) haut = bas;
      else bas = haut;
      this.curseurMin.value = String(bas);
      this.curseurMax.value = String(haut);
    }

    this.#ecrireChamps(bas, haut);
    this.#rafraichir();
  };

  /**
   * Saisie manuelle dans les champs chiffrés : on ne réécrit pas le champ (le
   * curseur de frappe sauterait), on ne fait que suivre avec les poignées.
   */
  #surSaisie = (evenement) => {
    const champ = evenement.target;
    if (champ !== this.champMin && champ !== this.champMax) return;

    const mineures = convertMoneyToMinorUnits(champ.value, this.devise);
    const minimum = Number(this.curseurMin.min);
    const maximum = Number(this.curseurMax.max);
    // Champ vidé : la borne retourne à son extrémité, donc plus de filtre.
    const valeur = mineures == null ? (champ === this.champMin ? minimum : maximum) : Math.round(mineures / this.diviseur);
    const borne = Math.min(Math.max(valeur, minimum), maximum);

    if (champ === this.champMin) {
      this.curseurMin.value = String(Math.min(borne, Number(this.curseurMax.value)));
    } else {
      this.curseurMax.value = String(Math.max(borne, Number(this.curseurMin.value)));
    }

    this.#majNoms(Number(this.curseurMin.value), Number(this.curseurMax.value));
    this.#rafraichir();
  };

  /**
   * Réaligne curseurs, champs et étiquettes sur les attributs du serveur après
   * un re-rendu de la section.
   */
  #resynchroniser() {
    const basServeur = this.curseurMin?.getAttribute('value');
    const hautServeur = this.curseurMax?.getAttribute('value');
    if (basServeur == null || hautServeur == null) return;

    this.curseurMin.value = basServeur;
    this.curseurMax.value = hautServeur;
    this.#ecrireChamps(Number(basServeur), Number(hautServeur));
    this.#rafraichir();
  }

  /**
   * Recopie l'intervalle dans les champs chiffrés et décide quelles bornes
   * participent réellement au filtre.
   */
  #ecrireChamps(bas, haut) {
    this.#ecrire(this.champMin, this.#formaterChamp(bas));
    this.#ecrire(this.champMax, this.#formaterChamp(haut));
    this.#majNoms(bas, haut);
  }

  #majNoms(bas, haut) {
    this.#nom(this.champMin, bas > Number(this.curseurMin.min));
    this.#nom(this.champMax, haut < Number(this.curseurMax.max));
  }

  #nom(champ, actif) {
    const nom = champ?.dataset.nom;
    if (!champ || !nom) return;
    if (actif) {
      if (champ.getAttribute('name') !== nom) champ.setAttribute('name', nom);
    } else if (champ.hasAttribute('name')) {
      champ.removeAttribute('name');
    }
  }

  #rafraichir() {
    const minimum = Number(this.curseurMin.min);
    const maximum = Number(this.curseurMax.max);
    const bas = Number(this.curseurMin.value);
    const haut = Number(this.curseurMax.value);
    const etendue = maximum - minimum || 1;

    this.style.setProperty('--depart', `${((bas - minimum) / etendue) * 100}%`);
    this.style.setProperty('--arrivee', `${((haut - minimum) / etendue) * 100}%`);

    // Réécrire les étiquettes réveille l'observateur : on n'écrit que si le
    // texte a réellement changé, sinon la boucle ne s'arrête jamais.
    this.#ecrireTexte(this.etiquetteMin, this.#formater(bas));
    this.#ecrireTexte(this.etiquetteMax, this.#formater(haut));
  }

  #ecrire(champ, texte) {
    if (champ && champ.value !== texte) champ.value = texte;
  }

  #ecrireTexte(element, texte) {
    if (element && element.textContent !== texte) element.textContent = texte;
  }

  /** Montant sans symbole, au format de la boutique : « 156,00 ». */
  #formaterChamp(valeur) {
    const mineures = convertMoneyToMinorUnits(String(valeur), this.devise);
    if (mineures == null) return String(valeur);
    return formatMoney(mineures, this.formatArgent, this.devise);
  }

  /** Montant avec devise pour les bornes affichées sous le rail : « 156 € ». */
  #formater(valeur) {
    try {
      return new Intl.NumberFormat(this.langue, {
        style: 'currency',
        currency: this.devise,
        maximumFractionDigits: 0,
      }).format(valeur);
    } catch {
      return String(valeur);
    }
  }
}

if (!customElements.get('cp-price-slider')) {
  customElements.define('cp-price-slider', CpPriceSlider);
}
